import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { App } from "antd";
import Toggle from "@/components/base/Toggle";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  addStudyCraAssignments,
  getStudy,
  getStudyErrorMessage,
  listAssignableCras,
  listStudyMembers,
  removeStudyCraAssignment,
  type Study,
  type StudyCraUser,
  type StudyMember,
  type StudyMemberSourceFilter,
} from "@/services/studies";
import { listStudySources, type StudySourceItem } from "@/services/studySource";
import {
  getStudyDgSyncConfig,
  isDgSyncInProgress,
  saveStudyDgSyncConfig,
  type DgSyncConfig as DgSyncConfigData,
} from "@/services/dgSync";

type TabKey = "members" | "sources";
type MemberSourceFilter = StudyMemberSourceFilter;
type CraSearchStatus = "idle" | "waiting" | "loading" | "ready";

// Distribution Group 同步配置在页面上的展示模型（由后端 DgSyncConfigItem 映射而来）。
export interface DgSyncConfig {
  dgEmail: string;
  includeNested: boolean;
  userScope: 0 | 1;
  description: string;
  status: "Enabled" | "Disabled";
  lastSyncTime: string;
  lastSyncStatus: "syncing" | "success" | "failed" | "idle";
  syncedUserCount: number;
  lastSyncError?: string;
}

// 同步进行中时的轮询间隔（毫秒）。
const DG_SYNC_POLL_INTERVAL_MS = 3000;

const CRA_SEARCH_DEBOUNCE_MS = 500;
const MEMBERS_PAGE_SIZE = 10;

const MEMBER_SOURCE_FILTERS = new Set<MemberSourceFilter>([
  "all",
  "auto",
  "manual",
  "teams",
  "sharepoint",
  "dg",
]);

// 详情页只持有后端 SQL 已合并、筛选和分页后的当前页成员。
interface DetailMember {
  assignmentId?: string;
  addedAt?: string;
  id: string;
  name: string;
  email: string;
  isManual: boolean;
  connectionTypes: Array<0 | 1>;
  hasDg: boolean;
}

function toDetailMember(member: StudyMember): DetailMember {
  return {
    assignmentId: member.assignmentId,
    addedAt: member.addedAt,
    id: member.id,
    name: member.name,
    email: member.email,
    isManual: member.manuallyAssigned,
    connectionTypes: member.connectionTypes,
    hasDg: member.hasDg,
  };
}

export default function StudyDetailPage() {
  const { t } = useTranslation();
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message } = App.useApp();

  const [study, setStudy] = useState<Study | null>(null);
  const [sources, setSources] = useState<StudySourceItem[]>([]);
  const [members, setMembers] = useState<DetailMember[]>([]);
  const [memberCount, setMemberCount] = useState(0);
  const [memberTotalCount, setMemberTotalCount] = useState(0);
  const [memberPage, setMemberPage] = useState(1);
  const [memberSearchInput, setMemberSearchInput] = useState("");
  const [memberKeyword, setMemberKeyword] = useState("");
  const [membersLoading, setMembersLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>("members");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [removeTarget, setRemoveTarget] = useState<DetailMember | null>(null);
  const [assignableCras, setAssignableCras] = useState<StudyCraUser[]>([]);
  const [craSearchStatus, setCraSearchStatus] = useState<CraSearchStatus>("idle");
  const [submitting, setSubmitting] = useState(false);
  const craSearchTimerRef = useRef<number | null>(null);
  const craSearchRequestRef = useRef(0);
  const memberRequestRef = useRef(0);
  const lastCraSearchKeywordRef = useRef("");

  // Distribution Group 同步相关状态。
  const [showDgModal, setShowDgModal] = useState(false);
  const [showDgDetail, setShowDgDetail] = useState(false);
  const [editingDg, setEditingDg] = useState(false);
  const [dgConfig, setDgConfig] = useState<DgSyncConfig | null>(null);
  const [dgToast, setDgToast] = useState<string | null>(null);
  const [dgSaving, setDgSaving] = useState(false);
  const dgSavingRef = useRef(false);
  const dgMountedRef = useRef(true);
  const dgPollGenerationRef = useRef(0);
  const dgPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 记录当前成员查询条件，供同步完成后刷新使用。
  const memberQueryRef = useRef<{ keyword: string; source: MemberSourceFilter }>({
    keyword: "",
    source: "all",
  });

  const requestedSourceFilter = searchParams.get("memberSource");
  const sourceFilter: MemberSourceFilter = MEMBER_SOURCE_FILTERS.has(
    requestedSourceFilter as MemberSourceFilter,
  )
    ? (requestedSourceFilter as MemberSourceFilter)
    : "all";

  const handleSourceFilterChange = (nextFilter: MemberSourceFilter) => {
    setMemberPage(1);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextFilter === "all") next.delete("memberSource");
      else next.set("memberSource", nextFilter);
      return next;
    });
  };

  const loadMembers = useCallback(
    async (id: string, nextPage: number, keyword: string, source: MemberSourceFilter) => {
      const requestId = ++memberRequestRef.current;
      setMembersLoading(true);
      try {
        const result = await listStudyMembers(id, {
          keyword,
          source,
          page: nextPage,
          limit: MEMBERS_PAGE_SIZE,
        });
        if (requestId !== memberRequestRef.current) return;
        setMembers(result.data.map(toDetailMember));
        setMemberCount(result.count);
        setMemberTotalCount(result.totalCount);
        setMemberPage(result.page);
      } catch (error) {
        if (requestId !== memberRequestRef.current) return;
        setMembers([]);
        setMemberCount(0);
        setMemberTotalCount(0);
        message.error(getStudyErrorMessage(error, t("studies.detail.membersLoadFailed")));
      } finally {
        if (requestId === memberRequestRef.current) setMembersLoading(false);
      }
    },
    [message, t],
  );

  const loadStudy = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const [detail, sourceList] = await Promise.all([getStudy(id), listStudySources()]);
        setStudy(detail);
        setSources(sourceList.data.filter((item) => item.studyId === id));
      } catch (error) {
        setNotFound(true);
        message.error(getStudyErrorMessage(error, t("studies.detailLoadFailed")));
      } finally {
        setLoading(false);
      }
    },
    [message, t],
  );

  useEffect(() => {
    if (!studyId) return;
    void loadStudy(studyId);
  }, [studyId, loadStudy]);

  useEffect(() => {
    if (!studyId) return;
    void loadMembers(studyId, memberPage, memberKeyword, sourceFilter);
  }, [studyId, memberPage, memberKeyword, sourceFilter, loadMembers]);

  useEffect(
    () => () => {
      memberRequestRef.current += 1;
    },
    [],
  );

  const groupedMembers = useMemo(() => {
    const groups: { label: string; members: DetailMember[] }[] = [];
    const autoSync = members.filter((m) => !m.isManual);
    const manual = members.filter((m) => m.isManual);
    if (autoSync.length > 0)
      groups.push({ label: t("studies.detail.tagAutoSync"), members: autoSync });
    if (manual.length > 0) groups.push({ label: t("studies.detail.tagManual"), members: manual });
    return groups;
  }, [members, t]);

  const handleMemberSearch = () => {
    setMemberPage(1);
    setMemberKeyword(memberSearchInput.trim());
  };

  const handleMemberSearchReset = () => {
    setMemberSearchInput("");
    setMemberKeyword("");
    setMemberPage(1);
  };

  // 同步当前成员查询条件到 ref，供同步完成后的刷新逻辑读取最新值。
  useEffect(() => {
    memberQueryRef.current = { keyword: memberKeyword, source: sourceFilter };
  }, [memberKeyword, sourceFilter]);

  // 把后端返回的 DG 配置映射为页面展示模型；Exists=false 表示尚未配置。
  const applyDgConfig = useCallback((item: DgSyncConfigData | null) => {
    if (!item || !item.exists) {
      setDgConfig(null);
      return;
    }
    setDgConfig({
      dgEmail: item.groupEmail ?? "",
      includeNested: item.includeNested,
      userScope: item.userScope ?? 0,
      description: item.description ?? "",
      status: item.isEnabled ? "Enabled" : "Disabled",
      lastSyncTime: item.lastSyncCompletedAt ?? "",
      lastSyncStatus: isDgSyncInProgress(item)
        ? "syncing"
        : item.lastSyncStatus === "Failed"
          ? "failed"
          : item.lastSyncStatus === "Succeeded"
            ? "success"
            : "idle",
      syncedUserCount: item.syncedUserCount ?? 0,
      lastSyncError: item.lastSyncError ?? undefined,
    });
  }, []);

  const stopDgPolling = useCallback(() => {
    dgPollGenerationRef.current += 1;
    if (dgPollTimerRef.current) {
      clearTimeout(dgPollTimerRef.current);
      dgPollTimerRef.current = null;
    }
  }, []);

  // 每 3 秒轮询一次 DG 配置，直到同步到达终态；终态时刷新成员列表。
  const startDgPolling = useCallback(() => {
    if (!dgMountedRef.current) return;
    stopDgPolling();
    const generation = dgPollGenerationRef.current;
    const tick = async () => {
      if (!studyId) return;
      try {
        const item = await getStudyDgSyncConfig(studyId);
        if (generation !== dgPollGenerationRef.current) return;
        applyDgConfig(item);
        if (isDgSyncInProgress(item)) {
          dgPollTimerRef.current = setTimeout(() => void tick(), DG_SYNC_POLL_INTERVAL_MS);
          return;
        }
        dgPollTimerRef.current = null;
        setDgToast(null);
        void loadMembers(studyId, 1, memberQueryRef.current.keyword, memberQueryRef.current.source);
      } catch {
        // 暂时读不到状态不终止观察；卸载/切换 Study 后丢弃迟到响应。
        if (generation === dgPollGenerationRef.current)
          dgPollTimerRef.current = setTimeout(() => void tick(), DG_SYNC_POLL_INTERVAL_MS);
      }
    };
    dgPollTimerRef.current = setTimeout(() => void tick(), DG_SYNC_POLL_INTERVAL_MS);
  }, [studyId, applyDgConfig, loadMembers, stopDgPolling]);

  // 加载当前 Study 已保存的 Distribution Group 同步配置；若同步仍在进行则恢复轮询。
  useEffect(() => {
    stopDgPolling();
    setDgToast(null);
    if (!studyId) return;
    let cancelled = false;
    (async () => {
      try {
        const item = await getStudyDgSyncConfig(studyId);
        if (cancelled) return;
        applyDgConfig(item);
        if (item.exists && isDgSyncInProgress(item)) {
          setDgToast(t("studies.detail.dgSyncingToast"));
          startDgPolling();
        }
      } catch {
        if (!cancelled) setDgConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId, t, applyDgConfig, startDgPolling, stopDgPolling]);

  // 页面卸载时清理未完成的轮询计时器。
  useEffect(() => {
    dgMountedRef.current = true;
    return () => {
      dgMountedRef.current = false;
      stopDgPolling();
    };
  }, [stopDgPolling]);

  // 保存 Distribution Group 配置：启用和禁用均触发同步任务并轮询。
  const handleSaveAndSyncDistribution = async (
    dgEmail: string,
    nested: boolean,
    description: string,
    status: "Enabled" | "Disabled",
    userScope: 0 | 1,
  ) => {
    if (!studyId || dgSavingRef.current || dgConfig?.lastSyncStatus === "syncing") return;
    dgSavingRef.current = true;
    setDgSaving(true);
    try {
      const result = await saveStudyDgSyncConfig(studyId, {
        groupEmail: dgEmail,
        includeNested: nested,
        userScope,
        isEnabled: status === "Enabled",
        description: description || null,
      });
      setShowDgModal(false);
      setEditingDg(false);
      setShowDgDetail(false);
      if (result.config) {
        applyDgConfig(result.config);
        setDgToast(null);
        void loadMembers(studyId, 1, memberQueryRef.current.keyword, memberQueryRef.current.source);
        return;
      }
      if (!result.triggered) throw new Error(t("studies.detail.dgSaveFailed"));
      // 202：同步任务已触发，先进入“同步中”展示状态再轮询。
      setDgConfig({
        dgEmail,
        includeNested: nested,
        userScope,
        description,
        status,
        lastSyncTime: dgConfig?.lastSyncTime ?? "",
        lastSyncStatus: "syncing",
        syncedUserCount: dgConfig?.syncedUserCount ?? 0,
      });
      setDgToast(t("studies.detail.dgSyncingToast"));
      startDgPolling();
    } catch (error) {
      message.error(getStudyErrorMessage(error, t("studies.detail.dgSaveFailed")));
      startDgPolling();
    } finally {
      dgSavingRef.current = false;
      setDgSaving(false);
    }
  };

  // 点击 DG Sync Settings：已有配置则打开详情，否则打开配置弹窗。
  const handleOpenDgSync = () => {
    setEditingDg(false);
    if (dgConfig) setShowDgDetail(true);
    else setShowDgModal(true);
  };

  // 在详情弹窗中切换启用/停用状态：用当前配置翻转 IsEnabled 后整体 POST。
  const handleToggleDgStatus = async () => {
    if (!dgConfig || !studyId || dgSavingRef.current || dgConfig.lastSyncStatus === "syncing")
      return;
    dgSavingRef.current = true;
    setDgSaving(true);
    const nextEnabled = dgConfig.status !== "Enabled";
    try {
      const result = await saveStudyDgSyncConfig(studyId, {
        groupEmail: dgConfig.dgEmail,
        includeNested: dgConfig.includeNested,
        userScope: dgConfig.userScope,
        isEnabled: nextEnabled,
        description: dgConfig.description || null,
      });
      if (result.config) {
        applyDgConfig(result.config);
        setDgToast(null);
        return;
      }
      if (!result.triggered) throw new Error(t("studies.detail.dgSaveFailed"));
      // 202：同步任务已触发，先进入“同步中”展示状态再轮询。
      setDgConfig({
        ...dgConfig,
        status: nextEnabled ? "Enabled" : "Disabled",
        lastSyncStatus: "syncing",
      });
      setDgToast(t("studies.detail.dgSyncingToast"));
      startDgPolling();
    } catch (error) {
      message.error(getStudyErrorMessage(error, t("studies.detail.dgSaveFailed")));
      startDgPolling();
    } finally {
      dgSavingRef.current = false;
      setDgSaving(false);
    }
  };

  const clearCraSearchTimer = useCallback(() => {
    if (craSearchTimerRef.current !== null) {
      window.clearTimeout(craSearchTimerRef.current);
      craSearchTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearCraSearchTimer();
      craSearchRequestRef.current += 1;
    },
    [clearCraSearchTimer],
  );

  const performCraSearch = useCallback(
    async (rawKeyword: string) => {
      const keyword = rawKeyword.trim();
      if (!study || !keyword) return;

      const requestId = ++craSearchRequestRef.current;
      lastCraSearchKeywordRef.current = keyword;
      setCraSearchStatus("loading");
      try {
        const users = await listAssignableCras(study.id, keyword);
        if (requestId !== craSearchRequestRef.current) return;
        setAssignableCras(users);
        setCraSearchStatus("ready");
      } catch (error) {
        if (requestId !== craSearchRequestRef.current) return;
        lastCraSearchKeywordRef.current = "";
        setAssignableCras([]);
        setCraSearchStatus("idle");
        message.error(getStudyErrorMessage(error, t("studies.craLoadFailed")));
      }
    },
    [message, study, t],
  );

  const handleAddSearchChange = (value: string) => {
    clearCraSearchTimer();
    craSearchRequestRef.current += 1;
    lastCraSearchKeywordRef.current = "";
    setAddSearch(value);
    setAssignableCras([]);

    if (!value.trim()) {
      setCraSearchStatus("idle");
      return;
    }

    setCraSearchStatus("waiting");
    craSearchTimerRef.current = window.setTimeout(() => {
      craSearchTimerRef.current = null;
      void performCraSearch(value);
    }, CRA_SEARCH_DEBOUNCE_MS);
  };

  const handleAddSearchBlur = () => {
    const keyword = addSearch.trim();
    clearCraSearchTimer();
    if (keyword && lastCraSearchKeywordRef.current !== keyword) {
      void performCraSearch(keyword);
    }
  };

  const closeAddModal = () => {
    clearCraSearchTimer();
    craSearchRequestRef.current += 1;
    lastCraSearchKeywordRef.current = "";
    setShowAddModal(false);
    setAddSearch("");
    setAssignableCras([]);
    setCraSearchStatus("idle");
    setSelectedUserIds(new Set());
  };

  const openAddModal = () => {
    clearCraSearchTimer();
    craSearchRequestRef.current += 1;
    lastCraSearchKeywordRef.current = "";
    setShowAddModal(true);
    setAddSearch("");
    setAssignableCras([]);
    setCraSearchStatus("idle");
    setSelectedUserIds(new Set());
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // 手动添加成员：使用增量接口追加选中用户对应的规范化 Email。
  const handleAddMembers = async () => {
    if (!study || selectedUserIds.size === 0) return;
    setSubmitting(true);
    try {
      await addStudyCraAssignments(study.id, Array.from(selectedUserIds));
      if (memberPage === 1) {
        await loadMembers(study.id, 1, memberKeyword, sourceFilter);
      } else {
        setMemberPage(1);
      }
      closeAddModal();
    } catch (error) {
      message.error(getStudyErrorMessage(error, t("studies.modal.saveFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  // 精确移除手动成员对应的 StudyCraAssignment，不影响 CRA 角色或其他关联。
  const handleRemoveMember = async () => {
    if (!study || !removeTarget?.assignmentId) return;
    setSubmitting(true);
    try {
      await removeStudyCraAssignment(study.id, removeTarget.assignmentId);
      if (memberPage === 1) {
        await loadMembers(study.id, 1, memberKeyword, sourceFilter);
      } else {
        setMemberPage(1);
      }
      setRemoveTarget(null);
    } catch (error) {
      message.error(getStudyErrorMessage(error, t("studies.modal.saveFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <i className="ri-loader-4-line text-2xl animate-spin text-gray-300"></i>
        </div>
      </AdminLayout>
    );
  }

  if (notFound || !study) {
    return (
      <AdminLayout>
        <div>
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => navigate("/admin/studies")}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              {t("common.back")}
            </button>
          </div>
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-10 h-10 flex items-center justify-center mb-3">
              <i className="ri-error-warning-line text-3xl text-gray-300"></i>
            </span>
            <p className="text-sm font-medium text-gray-400">{t("studies.detail.notFound")}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Back */}
        <div className="flex items-center gap-2 mb-5">
          <button
            onClick={() => navigate("/admin/studies")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            {t("common.back")}
          </button>
        </div>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2">
            {study.name}
            <span
              className="text-[11px] font-mono px-2 py-0.5 rounded"
              style={{ backgroundColor: "rgba(131,0,81,0.06)", color: "#830051" }}
            >
              {study.code}
            </span>
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between border-b border-gray-100 mb-4">
          <div className="flex items-center gap-0">
            <button
              onClick={() => setActiveTab("members")}
              className={`px-5 py-2.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-[1px] ${
                activeTab === "members"
                  ? "text-gray-900 border-gray-900"
                  : "text-gray-400 border-transparent hover:text-gray-600"
              }`}
            >
              {t("studies.detail.tabMembers")}
              <span className="ml-1.5 text-[11px] text-gray-400">{memberTotalCount}</span>
            </button>
            <button
              onClick={() => setActiveTab("sources")}
              className={`px-5 py-2.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-[1px] ${
                activeTab === "sources"
                  ? "text-gray-900 border-gray-900"
                  : "text-gray-400 border-transparent hover:text-gray-600"
              }`}
            >
              {t("studies.detail.tabSources")}
              <span className="ml-1.5 text-[11px] text-gray-400">{sources.length}</span>
            </button>
          </div>
          {activeTab === "members" && (
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {dgToast ? (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap"
                  style={{ backgroundColor: "rgba(131,0,81,0.06)", color: "#830051" }}
                >
                  <i
                    className="ri-loader-4-line animate-spin text-xs"
                    style={{ color: "#830051" }}
                  ></i>
                  <span className="truncate max-w-[260px]">{t("studies.detail.dgSyncing")}</span>
                </div>
              ) : dgConfig && dgConfig.lastSyncStatus === "syncing" ? (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium whitespace-nowrap mr-1"
                  style={{ color: "#830051" }}
                >
                  <i
                    className="ri-loader-4-line animate-spin text-xs"
                    style={{ color: "#830051" }}
                  ></i>
                  {t("studies.detail.dgSyncing")}
                </span>
              ) : (
                dgConfig && (
                  <span className="text-[11px] text-gray-500 whitespace-nowrap mr-1">
                    {t("studies.detail.dgLastSynced")}: {dgConfig.lastSyncTime}
                  </span>
                )
              )}
              <button
                onClick={handleOpenDgSync}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 border"
                style={{ backgroundColor: "#fff", color: "#830051", borderColor: "#830051" }}
              >
                <i className="ri-settings-line text-xs"></i>
                {t("studies.detail.dgSync")}
              </button>
              <button
                onClick={() => void openAddModal()}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ backgroundColor: "#830051", color: "#fff" }}
              >
                <i className="ri-add-line text-xs"></i>
                {t("studies.detail.addMember")}
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === "members" ? (
          <MembersTab
            groupedMembers={groupedMembers}
            membersCount={memberTotalCount}
            filteredCount={memberCount}
            loading={membersLoading}
            page={memberPage}
            pageSize={MEMBERS_PAGE_SIZE}
            searchInput={memberSearchInput}
            hasActiveSearch={memberKeyword !== ""}
            sourceFilter={sourceFilter}
            onSourceFilterChange={handleSourceFilterChange}
            onSearchInputChange={setMemberSearchInput}
            onSearch={handleMemberSearch}
            onResetSearch={handleMemberSearchReset}
            onPageChange={setMemberPage}
            onAdd={() => void openAddModal()}
            onRemove={(m) => setRemoveTarget(m)}
            onClickMember={(m) => navigate("/admin/users", { state: { searchUser: m.email } })}
          />
        ) : (
          <SourcesTab
            sources={sources}
            onView={(sourceId) =>
              navigate(`/admin/settings/study-source?sourceId=${encodeURIComponent(sourceId)}`)
            }
          />
        )}

        {/* Add Member Modal */}
        {showAddModal && (
          <AddMemberModal
            search={addSearch}
            onSearchChange={handleAddSearchChange}
            onSearchBlur={handleAddSearchBlur}
            searchStatus={craSearchStatus}
            users={assignableCras}
            selectedUserIds={selectedUserIds}
            onToggleUser={toggleUserSelection}
            submitting={submitting}
            onConfirm={() => void handleAddMembers()}
            onClose={closeAddModal}
          />
        )}

        {/* Remove Confirm Modal */}
        {removeTarget && (
          <RemoveConfirmModal
            member={removeTarget}
            submitting={submitting}
            onConfirm={() => void handleRemoveMember()}
            onClose={() => setRemoveTarget(null)}
          />
        )}

        {/* Distribution Group Sync Config Modal */}
        {showDgModal && (
          <DistributionGroupModal
            initial={editingDg ? dgConfig : null}
            saving={dgSaving}
            onConfirm={handleSaveAndSyncDistribution}
            onClose={() => {
              setShowDgModal(false);
              setEditingDg(false);
            }}
          />
        )}

        {/* Distribution Group Sync Detail Modal */}
        {showDgDetail && dgConfig && (
          <DistributionGroupDetailModal
            config={dgConfig}
            saving={dgSaving}
            onEdit={() => {
              setShowDgDetail(false);
              setEditingDg(true);
              setShowDgModal(true);
            }}
            onToggleStatus={handleToggleDgStatus}
            onClose={() => setShowDgDetail(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// ── Members Tab ──────────────────────────────────────────────────────────────
function MembersTab({
  groupedMembers,
  membersCount,
  filteredCount,
  loading,
  page,
  pageSize,
  searchInput,
  hasActiveSearch,
  sourceFilter,
  onSourceFilterChange,
  onSearchInputChange,
  onSearch,
  onResetSearch,
  onPageChange,
  onAdd,
  onRemove,
  onClickMember,
}: {
  groupedMembers: { label: string; members: DetailMember[] }[];
  membersCount: number;
  filteredCount: number;
  loading: boolean;
  page: number;
  pageSize: number;
  searchInput: string;
  hasActiveSearch: boolean;
  sourceFilter: MemberSourceFilter;
  onSourceFilterChange: (v: MemberSourceFilter) => void;
  onSearchInputChange: (value: string) => void;
  onSearch: () => void;
  onResetSearch: () => void;
  onPageChange: (page: number) => void;
  onAdd: () => void;
  onRemove: (m: DetailMember) => void;
  onClickMember: (m: DetailMember) => void;
}) {
  const { t } = useTranslation();
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filteredCount);
  const pageItems = Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter((item) => item === 1 || item === totalPages || Math.abs(item - currentPage) <= 2)
    .reduce<Array<number | "ellipsis">>((items, item, index, visiblePages) => {
      if (index > 0 && item - visiblePages[index - 1] > 1) items.push("ellipsis");
      items.push(item);
      return items;
    }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const sourceOptions = [
    { value: "all", label: t("studies.detail.sourceFilter.all") },
    { value: "auto", label: t("studies.detail.tagAutoSync") },
    { value: "manual", label: t("studies.detail.tagManual") },
    { value: "teams", label: t("studies.detail.memberSource.teams") },
    { value: "sharepoint", label: t("studies.detail.memberSource.sharepoint") },
    { value: "dg", label: t("studies.detail.memberSource.dg") },
  ] satisfies Array<{ value: MemberSourceFilter; label: string }>;

  const selectedLabel = sourceOptions.find((option) => option.value === sourceFilter)?.label ?? "";

  const renderFilter = () => (
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSearch();
        }}
      >
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="search"
            value={searchInput}
            onChange={(event) => onSearchInputChange(event.target.value)}
            placeholder={t("studies.detail.memberSearchPlaceholder")}
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-gray-300 bg-white w-64"
          />
        </div>
        <button
          type="submit"
          className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: "#830051" }}
        >
          {t("studies.searchBtn")}
        </button>
        {hasActiveSearch && (
          <button
            type="button"
            onClick={onResetSearch}
            className="px-2 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            {t("studies.reset")}
          </button>
        )}
      </form>

      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap">
          {t("studies.detail.colSource")}:
        </span>
        <div className="relative" ref={filterRef}>
          <button
            type="button"
            onClick={() => setFilterOpen(!filterOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span
              className={sourceFilter === "all" ? "text-gray-400" : ""}
              style={sourceFilter !== "all" ? { color: "#830051" } : {}}
            >
              {selectedLabel}
            </span>
            <i
              className={`ri-arrow-down-s-line text-[11px] text-gray-400 transition-transform ${filterOpen ? "rotate-180" : ""}`}
            ></i>
          </button>
          {filterOpen && (
            <div
              className="absolute top-full right-0 mt-1 bg-white border border-gray-100 rounded-lg overflow-hidden z-50"
              style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: "150px" }}
            >
              {sourceOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSourceFilterChange(option.value);
                    setFilterOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-xs transition-colors cursor-pointer whitespace-nowrap hover:bg-gray-50 ${
                    sourceFilter === option.value ? "font-medium" : "text-gray-500"
                  }`}
                  style={
                    sourceFilter === option.value
                      ? { color: "#830051", backgroundColor: "rgba(131,0,81,0.04)" }
                      : {}
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {(sourceFilter !== "all" || hasActiveSearch) && (
          <span className="text-[11px] text-gray-300">
            {t("studies.detail.membersCount", { count: filteredCount })}
          </span>
        )}
      </div>
    </div>
  );

  if (!loading && membersCount === 0) {
    return (
      <div>
        {renderFilter()}
        <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
          <span className="w-9 h-9 flex items-center justify-center mb-3">
            <i className="ri-group-line text-2xl text-gray-300"></i>
          </span>
          <p className="text-sm font-medium text-gray-400">{t("studies.detail.membersEmpty")}</p>
          <p className="text-xs text-gray-300 mt-1 max-w-[320px]">
            {t("studies.detail.membersEmptyHint")}
          </p>
          <button
            onClick={onAdd}
            className="mt-4 px-4 py-1.5 text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#830051", color: "#fff" }}
          >
            <i className="ri-user-add-line text-xs mr-1"></i>
            {t("studies.detail.addMember")}
          </button>
        </div>
      </div>
    );
  }

  if (!loading && filteredCount === 0) {
    return (
      <div>
        {renderFilter()}
        <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
          <span className="w-9 h-9 flex items-center justify-center mb-3">
            <i className="ri-filter-off-line text-2xl text-gray-300"></i>
          </span>
          <p className="text-sm font-medium text-gray-400">{t("studies.detail.filterNoResults")}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {renderFilter()}
      <div className="border border-gray-100 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: "#fafafa" }}>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
                {t("studies.detail.colName")}
              </th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
                {t("studies.detail.colEmail")}
              </th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
                {t("studies.detail.colSource")}
              </th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
                {t("studies.detail.colAddedAt")}
              </th>
              <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
                {t("studies.detail.colAction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-400">
                  <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
                  <div>{t("common.loading")}</div>
                </td>
              </tr>
            ) : (
              groupedMembers.map((group, gi) => {
                const isLastGroup = gi === groupedMembers.length - 1;
                return group.members.map((m, mi) => {
                  const isLastRow = isLastGroup && mi === group.members.length - 1;
                  const isAutoSync = !m.isManual;
                  return (
                    <tr
                      key={m.id || m.email || `${m.name}-${mi}`}
                      className="transition-colors"
                      style={{
                        borderBottom: isLastRow ? "none" : "1px solid #f3f4f6",
                        backgroundColor: isAutoSync ? "#fafafa" : "transparent",
                      }}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onClickMember(m)}
                          className="text-xs font-medium text-gray-800 hover:text-[#830051] cursor-pointer text-left transition-colors"
                        >
                          {m.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">{m.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {m.isManual && (
                            <span
                              className="inline-block text-[10px] font-medium px-2 py-0.5 rounded whitespace-nowrap"
                              style={{
                                backgroundColor: "rgba(131,0,81,0.08)",
                                color: "#830051",
                                border: "1px solid rgba(131,0,81,0.15)",
                              }}
                            >
                              {t("studies.detail.tagManual")}
                            </span>
                          )}
                          {[
                            ...(m.connectionTypes.includes(0) ? ["teams"] : []),
                            ...(m.connectionTypes.includes(1) ? ["sharepoint"] : []),
                            ...(m.hasDg ? ["dg"] : []),
                          ].map((source) => (
                            <span
                              key={source}
                              className="inline-block text-[10px] font-medium px-2 py-0.5 rounded whitespace-nowrap"
                              style={{
                                backgroundColor: "#f3f4f6",
                                color: "#6b7280",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              {t(`studies.detail.memberSource.${source}`)}
                            </span>
                          ))}
                          {!m.isManual && m.connectionTypes.length === 0 && !m.hasDg && (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {m.addedAt || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end">
                          {isAutoSync ? (
                            <span className="text-[10px] text-gray-300 italic whitespace-nowrap">
                              {t("studies.detail.tagAutoSync")}
                            </span>
                          ) : (
                            <button
                              onClick={() => onRemove(m)}
                              aria-label={`${t("studies.detail.removeMemberTitle")}: ${m.name}`}
                              className="w-6 h-6 flex items-center justify-center rounded-md cursor-pointer hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
                            >
                              <i className="ri-delete-bin-line text-xs"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                });
              })
            )}
          </tbody>
        </table>
      </div>
      {!loading && filteredCount > pageSize && (
        <div className="flex items-center justify-between gap-3 px-1 py-3">
          <span className="text-xs text-gray-400">
            {t("studies.pagination", {
              from: pageStart + 1,
              to: pageEnd,
              total: filteredCount,
            })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t("studies.detail.paginationPrevious")}
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {pageItems.map((item, index) =>
              item === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="px-1 text-xs text-gray-400">
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  aria-label={t("studies.detail.paginationPage", { page: item })}
                  aria-current={item === currentPage ? "page" : undefined}
                  onClick={() => onPageChange(item)}
                  className={`min-w-[28px] px-2 py-1 text-xs rounded-lg border cursor-pointer transition-colors ${
                    item === currentPage
                      ? "text-white border-[#830051]"
                      : "text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                  style={item === currentPage ? { backgroundColor: "#830051" } : undefined}
                >
                  {item}
                </button>
              ),
            )}
            <button
              type="button"
              aria-label={t("studies.detail.paginationNext")}
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sources Tab ──────────────────────────────────────────────────────────────
function SourcesTab({
  sources,
  onView,
}: {
  sources: StudySourceItem[];
  onView: (sourceId: string) => void;
}) {
  const { t } = useTranslation();

  if (sources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
        <span className="w-9 h-9 flex items-center justify-center mb-3">
          <i className="ri-folder-line text-2xl text-gray-300"></i>
        </span>
        <p className="text-sm font-medium text-gray-400">{t("studies.detail.sourcesEmpty")}</p>
        <p className="text-xs text-gray-300 mt-1 max-w-[320px]">
          {t("studies.detail.sourcesEmptyHint")}
        </p>
      </div>
    );
  }

  const connectionLabel = (type: 0 | 1) =>
    type === 0
      ? t("studies.detail.memberSource.teams")
      : t("studies.detail.memberSource.sharepoint");

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: "#fafafa" }}>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.detail.colChannelName")}
            </th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.detail.colConnection")}
            </th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.detail.colUserListSync")}
            </th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-500 border-b border-gray-100">
              {t("studies.detail.colAction")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sources.map((src, idx) => {
            const isLast = idx === sources.length - 1;
            const channelName = src.channelNameFromUrl || src.sourceSite || src.sourceUrl || "-";
            return (
              <tr
                key={src.id}
                className="hover:bg-gray-50/60 transition-colors"
                style={{ borderBottom: isLast ? "none" : "1px solid #f3f4f6" }}
              >
                <td className="px-4 py-3">
                  <span className="text-xs font-medium text-gray-800">{channelName}</span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-block text-[10px] font-medium px-2 py-0.5 rounded whitespace-nowrap"
                    style={{
                      backgroundColor: "#f3f4f6",
                      color: "#6b7280",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    {connectionLabel(src.connectionType)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[11px] font-medium ${src.userListSync ? "" : "text-gray-400"}`}
                    style={src.userListSync ? { color: "#830051" } : {}}
                  >
                    {src.userListSync
                      ? t("studies.detail.userListSourceYes")
                      : t("studies.detail.userListSourceNo")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-[#830051] cursor-pointer transition-colors whitespace-nowrap"
                    onClick={() => onView(src.id)}
                  >
                    <i className="ri-eye-line text-[11px]"></i>
                    {t("studies.detail.view")}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Add Member Modal ─────────────────────────────────────────────────────────
function AddMemberModal({
  search,
  onSearchChange,
  onSearchBlur,
  searchStatus,
  users,
  selectedUserIds,
  onToggleUser,
  submitting,
  onConfirm,
  onClose,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  onSearchBlur: () => void;
  searchStatus: CraSearchStatus;
  users: StudyCraUser[];
  selectedUserIds: Set<string>;
  onToggleUser: (id: string) => void;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("studies.detail.addMemberTitle")}
        className="bg-white rounded-xl w-[420px] max-w-[90vw] overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {t("studies.detail.addMemberTitle")}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <div className="px-5 py-3">
          <p className="text-xs text-gray-400 leading-relaxed">
            {t("studies.detail.addMemberDesc")}
          </p>
          <div className="relative mt-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder={t("studies.detail.addMemberSearch")}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onBlur={onSearchBlur}
              className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-300 w-full"
            />
          </div>
        </div>

        <div className="px-5 min-h-[88px] max-h-[240px] overflow-y-auto">
          {searchStatus === "idle" ? (
            <p className="text-xs text-gray-300 text-center py-6">
              {t("studies.detail.addMemberSearchHint")}
            </p>
          ) : searchStatus === "waiting" ? null : searchStatus === "loading" ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-400">
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              {t("studies.detail.addMemberSearching")}
            </div>
          ) : users.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">
              {t("studies.detail.addMemberNoResults")}
            </p>
          ) : (
            <div className="flex flex-col gap-0.5 pb-2">
              {users.map((u) => {
                const selected = selectedUserIds.has(u.id);
                const disabled = u.isAssigned === true;
                return (
                  <button
                    key={u.id}
                    onClick={() => onToggleUser(u.id)}
                    disabled={disabled}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                      disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        selected
                          ? "border-transparent"
                          : disabled
                            ? "border-gray-200 bg-gray-50"
                            : "border-gray-300"
                      }`}
                      style={selected ? { backgroundColor: "#830051" } : {}}
                    >
                      {selected && <i className="ri-check-line text-[10px] text-white"></i>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{u.name}</p>
                      <p className="text-[11px] text-gray-400 truncate">{u.email}</p>
                    </div>
                    {disabled && (
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">
                        {t("studies.detail.addMemberAlreadyAssigned")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">
            {selectedUserIds.size > 0 ? `${selectedUserIds.size} selected` : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={onConfirm}
              disabled={selectedUserIds.size === 0 || submitting}
              className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#830051" }}
            >
              {t("studies.detail.addMemberConfirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Remove Confirm Modal ─────────────────────────────────────────────────────
function RemoveConfirmModal({
  member,
  submitting,
  onConfirm,
  onClose,
}: {
  member: DetailMember;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("studies.detail.removeMemberTitle")}
        className="bg-white rounded-xl w-[380px] max-w-[90vw] overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            {t("studies.detail.removeMemberTitle")}
          </h3>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            {t("studies.detail.removeMemberDesc", { name: member.name })}
          </p>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="px-4 py-2 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("studies.detail.removeMemberConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Distribution Group Sync Modal ────────────────────────────────────────────
function DistributionGroupModal({
  initial,
  saving,
  onConfirm,
  onClose,
}: {
  initial?: DgSyncConfig | null;
  saving: boolean;
  onConfirm: (
    dgEmail: string,
    nested: boolean,
    description: string,
    status: "Enabled" | "Disabled",
    userScope: 0 | 1,
  ) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [dgEmail, setDgEmail] = useState(initial?.dgEmail ?? "");
  const [includeNested, setIncludeNested] = useState(initial?.includeNested ?? true);
  const [userScope, setUserScope] = useState<0 | 1>(initial?.userScope ?? 0);
  const [description, setDescription] = useState(initial?.description ?? "");
  const [status, setStatus] = useState<"Enabled" | "Disabled">(initial?.status ?? "Enabled");
  const [nestedTipPos, setNestedTipPos] = useState<{ top: number; left: number } | null>(null);
  const nestedTipTriggerRef = useRef<HTMLDivElement>(null);
  const [showNestedTip, setShowNestedTip] = useState(false);

  const canConfirm = !saving && dgEmail.trim().length > 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(dgEmail.trim(), includeNested, description.trim(), status, userScope);
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
    >
      <div
        className="bg-white rounded-xl w-[680px] max-w-[90vw] overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {t("studies.detail.dgModalTitle")}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-5 max-h-[60vh] overflow-y-auto">
          {/* Distribution Group Email */}
          <div className="flex items-start gap-4">
            <label className="w-36 shrink-0 text-xs font-medium text-gray-700 pt-2">
              {t("studies.detail.dgEmail")}
              <span className="text-red-400 ml-0.5">*</span>
            </label>
            <input
              type="email"
              placeholder={t("studies.detail.dgEmailPlaceholder")}
              value={dgEmail}
              onChange={(e) => setDgEmail(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-400"
            />
          </div>

          {/* Include Nested Groups */}
          <div className="flex items-start gap-4">
            <div className="w-36 shrink-0" />
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIncludeNested(!includeNested)}
                className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
                  includeNested ? "border-transparent" : "border-gray-300"
                }`}
                style={includeNested ? { backgroundColor: "#830051" } : {}}
              >
                {includeNested && <i className="ri-check-line text-[10px] text-white"></i>}
              </button>
              <span className="text-xs text-gray-700">{t("studies.detail.dgIncludeNested")}</span>
              <div
                className="relative"
                ref={nestedTipTriggerRef}
                onMouseEnter={() => {
                  if (nestedTipTriggerRef.current) {
                    const rect = nestedTipTriggerRef.current.getBoundingClientRect();
                    setNestedTipPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
                  }
                  setShowNestedTip(true);
                }}
                onMouseLeave={() => setShowNestedTip(false)}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center cursor-help shrink-0">
                  <i className="ri-question-line text-sm" style={{ color: "#830051" }}></i>
                </span>
              </div>
            </div>
          </div>

          {/* User Scope */}
          <div className="flex items-start gap-4">
            <span className="w-36 shrink-0 text-xs font-medium text-gray-700 pt-2">
              {t("sourceForm.userScope")}
            </span>
            <div
              role="radiogroup"
              aria-label={t("sourceForm.userScope")}
              className="flex flex-wrap gap-5 py-2"
            >
              {(
                [
                  { value: 1, label: t("sourceForm.userScope.internalExternal") },
                  { value: 0, label: t("sourceForm.userScope.internalOnly") },
                ] as Array<{ value: 0 | 1; label: string }>
              ).map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="radio"
                    name="study-dg-user-scope"
                    value={option.value}
                    checked={userScope === option.value}
                    onChange={() => setUserScope(option.value)}
                    disabled={saving}
                    className="h-4 w-4 accent-[#830051]"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Sync Status */}
          <div className="flex items-start gap-4">
            <label className="w-36 shrink-0 text-xs font-medium text-gray-700 pt-0.5">
              {t("studies.detail.dgStatus")}
            </label>
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStatus(status === "Enabled" ? "Disabled" : "Enabled")}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${
                    status === "Enabled" ? "" : "bg-gray-200"
                  }`}
                  style={status === "Enabled" ? { backgroundColor: "#830051" } : {}}
                  aria-pressed={status === "Enabled"}
                >
                  <span
                    className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform"
                    style={{
                      transform: status === "Enabled" ? "translateX(16px)" : "translateX(0)",
                    }}
                  />
                </button>
                <span
                  className="text-xs font-medium"
                  style={{ color: status === "Enabled" ? "#830051" : "#dc2626" }}
                >
                  {status === "Enabled"
                    ? t("studies.detail.dgStatusEnabled")
                    : t("studies.detail.dgStatusDisabled")}
                </span>
              </div>
              <span className="text-[11px] text-gray-500">
                {status === "Enabled"
                  ? t("studies.detail.dgStatusEnabledDesc")
                  : t("studies.detail.dgStatusDisabledDesc")}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="flex items-start gap-4">
            <label className="w-36 shrink-0 text-xs font-medium text-gray-700 pt-2">
              {t("studies.detail.dgDescription")}
            </label>
            <div className="flex-1 min-w-0 flex flex-col">
              <textarea
                placeholder={t("studies.detail.dgDescriptionPlaceholder")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-400 resize-none"
              />
              <p className="text-[10px] text-gray-300 mt-1 text-right">{description.length}/500</p>
            </div>
          </div>
        </div>

        {showNestedTip && nestedTipPos && (
          <div
            className="fixed z-[99999] px-3.5 py-2.5 bg-white text-gray-700 text-[11px] rounded-lg leading-relaxed border border-gray-200 w-64"
            style={{
              top: nestedTipPos.top,
              left: nestedTipPos.left,
              transform: "translate(-50%, -100%)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            }}
            onMouseEnter={() => setShowNestedTip(true)}
            onMouseLeave={() => setShowNestedTip(false)}
          >
            {t("studies.detail.dgNestedTooltip")}
            <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-white" />
            <span
              className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent"
              style={{ borderTopColor: "#e5e7eb", marginTop: "1px" }}
            />
          </div>
        )}

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#830051" }}
          >
            {status === "Enabled" ? t("studies.detail.dgSaveAndSync") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Distribution Group Sync Detail Modal ─────────────────────────────────────
function DistributionGroupDetailModal({
  config,
  saving,
  onEdit,
  onToggleStatus,
  onClose,
}: {
  config: DgSyncConfig;
  saving: boolean;
  onEdit: () => void;
  onToggleStatus: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const enabled = config.status === "Enabled";
  const isSyncing = config.lastSyncStatus === "syncing";
  const [confirmingToggle, setConfirmingToggle] = useState(false);

  const handleToggleClick = () => {
    setConfirmingToggle(true);
  };

  const handleConfirmToggle = () => {
    setConfirmingToggle(false);
    onToggleStatus();
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
    >
      <div
        className="bg-white rounded-xl w-[680px] max-w-[90vw] overflow-hidden relative"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.14)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {t("studies.detail.dgDetailTitle")}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer text-gray-400 shrink-0"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-4 max-h-[60vh] overflow-y-auto relative">
          {isSyncing && (
            <div
              className="flex flex-col gap-2 px-3 py-2.5 rounded-lg"
              style={{
                backgroundColor: "rgba(131,0,81,0.06)",
                border: "1px solid rgba(131,0,81,0.2)",
              }}
            >
              <div className="flex items-center gap-2">
                <i
                  className="ri-loader-2-line animate-spin text-sm"
                  style={{ color: "#830051" }}
                ></i>
                <span className="text-[11px] font-medium" style={{ color: "#830051" }}>
                  {t("studies.detail.dgSyncingHint")}
                </span>
              </div>
            </div>
          )}
          <InfoRow label={t("studies.detail.dgEmail")} value={config.dgEmail} />
          <InfoRow
            label={t("studies.detail.dgIncludeNested")}
            value={config.includeNested ? t("studies.detail.dgYes") : t("studies.detail.dgNo")}
          />

          {/* User Scope */}
          <InfoRow
            label={t("sourceForm.userScope")}
            value={t(
              config.userScope === 1
                ? "sourceForm.userScope.internalExternal"
                : "sourceForm.userScope.internalOnly",
            )}
          />

          {/* Status */}
          <div className="flex items-start gap-3">
            <span className="w-36 shrink-0 text-xs font-medium text-gray-500 pt-0.5">
              {t("studies.detail.dgStatus")}
            </span>
            <div className="flex items-center gap-3">
              <Toggle
                value={enabled}
                disabled={isSyncing || saving}
                ariaLabel={t("studies.detail.dgStatus")}
                onChange={handleToggleClick}
              />
              <span
                className="text-sm font-medium whitespace-nowrap"
                style={{ color: enabled ? "#830051" : "#dc2626" }}
              >
                {t(enabled ? "studies.detail.dgStatusEnabled" : "studies.detail.dgStatusDisabled")}
              </span>
            </div>
          </div>

          {/* Description */}
          <InfoRow label={t("studies.detail.dgDescription")} value={config.description || "—"} />

          <div className="border-t border-gray-100 pt-4 flex flex-col gap-4">
            <InfoRow
              label={t("studies.detail.dgLastSyncTime")}
              value={isSyncing ? "—" : config.lastSyncTime || "—"}
            />
            <div className="flex items-start gap-3">
              <span className="w-36 shrink-0 text-xs font-medium text-gray-500 pt-0.5">
                {t("studies.detail.dgLastSyncStatus")}
              </span>
              {isSyncing ? (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ backgroundColor: "rgba(131,0,81,0.08)", color: "#830051" }}
                >
                  <i
                    className="ri-loader-2-line animate-spin text-[10px]"
                    style={{ color: "#830051" }}
                  ></i>
                  {t("studies.detail.dgSyncing")}
                </span>
              ) : config.lastSyncStatus === "idle" ? (
                <span className="text-xs text-gray-400">—</span>
              ) : (
                <div className="flex flex-col gap-1">
                  <span
                    className={`inline-block self-start text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                      config.lastSyncStatus === "success"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {config.lastSyncStatus === "success"
                      ? t("studies.detail.dgSyncSuccess")
                      : t("studies.detail.dgSyncFailed")}
                  </span>
                  {config.lastSyncStatus === "failed" && config.lastSyncError && (
                    <span className="text-[11px] text-red-500">{config.lastSyncError}</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-start gap-3">
              <span className="w-36 shrink-0 text-xs font-medium text-gray-500 pt-0.5">
                {t("studies.detail.dgSyncedUserCount")}
              </span>
              <span className="text-xs text-gray-700 font-medium">
                {isSyncing ? "—" : config.syncedUserCount}
              </span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2 relative">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium rounded-lg border cursor-pointer whitespace-nowrap transition-colors hover:bg-[rgba(131,0,81,0.04)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            style={{ color: "#830051", borderColor: "rgba(131,0,81,0.25)" }}
          >
            {t("common.close")}
          </button>
          <button
            onClick={onEdit}
            disabled={isSyncing || saving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
            style={{ backgroundColor: "#830051" }}
          >
            <i className="ri-edit-line text-sm"></i>
            {t("studies.detail.dgEdit")}
          </button>
        </div>

        {/* 启用或停用同步的确认层 */}
        {confirmingToggle && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(255,255,255,0.85)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-white rounded-xl w-[380px] max-w-[85%] overflow-hidden"
              style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
            >
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t(enabled ? "studies.detail.dgDisableTitle" : "syncConfig.enableSyncTitle")}
                </h3>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-500 leading-relaxed">
                  {t(enabled ? "studies.detail.dgDisableDesc" : "syncConfig.enableSyncDesc")}
                </p>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => setConfirmingToggle(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleConfirmToggle}
                  className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: enabled ? "#dc2626" : "#16a34a" }}
                >
                  {t(enabled ? "studies.detail.dgDisable" : "studies.detail.dgEnable")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── DG 详情弹窗的信息行辅助组件 ───────────────────────────────────────────────
function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-36 shrink-0 text-xs font-medium text-gray-500 pt-0.5">{label}</span>
      <span className="flex-1 text-xs text-gray-700 break-words">{value}</span>
    </div>
  );
}
